import React, { useMemo, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import { ProgressSpinner } from "primereact/progressspinner";
import { SmartForm } from "ui-kit/SmartForm";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Platforms } from "./Platforms";
import { digitalFingerprintingSystems } from "./data";
import { decorators } from "./utils";
import { FormValues } from "./types";
import { usePlatforms } from "../hooks/usePlatforms";
import { usePlatformGroups } from "../hooks/usePlatformGroups";
import { useCountries } from "../hooks/useCountries";
import { useCountryGroup } from "../hooks/useCountryGroup";
import { useRelease } from "../hooks/useRelease";
import { DEFAULT_PLATFORM_GROUP } from "./constants";
import { DistributionCountry } from "./DistributionCountry";
import { isNotObjectEmpty, structuredValidator } from "utils/validation";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isArray } from "lodash";
import Danger from '../../../assets/danger.svg'
import { runDraftSaveIfNeeded, setDraftSaveGuard } from "../draftSaveGuard";


interface PublishProps {
  release: FormValues;
}

interface Country {
  id: number;
  name: string;
  iso: string;
  continent: string;
}

interface CheckedItem {
  checked: boolean;
  partialChecked: boolean;
}

export const Publish: React.FC<PublishProps> = ({ release }) => {
  const { id } = useParams();
  const UGC_PLATFORM_NAMES = ["Youtube Content ID", "TikTok Fingerprinting", "Soundcloud", 'Facebook/Instagram', 'Facebook Fingerprinting', 'MusicMatch'];

  const { platforms, fetching: platformsFetching } = usePlatforms();
  const [allData, setAllData] = useState(false);
  const { updateRelease } = useRelease();
  const [result, setResult] = useState({});
  const [idData, setIdData] = useState<string[]>([]);
  const [ugcData, setUgcData] = useState<string[]>([])
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [showHint, setShowHint] = useState(false);
  const [validButton, setValidButton] = useState(false);
  const [selectedValuesCountry, setSelectedValuesCountry] = useState<string[]>([]);
  


  const navigate = useNavigate();
  const location = useLocation();

  const { platformGroups, fetching: platformGroupsFetching, creating: platformGroupsCreating, createPlatformGroup, removePlatformGroup, upDatePlatformGroup, } = usePlatformGroups();
  const { countries, fetching: countryFetching } = useCountries();
  const { creating: countryGroupsCreating, fetching: countryGroupsFetching, countryGroups, createCountryGroup, removeCountryGroup, updateCountryGroup } = useCountryGroup();

  function extractSecondNumber(arr: any[]) {
    return arr.map(item => {
      if (item.includes('-')) {
        return parseInt(item.split('-')[1], 10);
      }
      return parseInt(item, 10);
    });
  }


  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    // На мобилке шаг 7 (Review) всегда открываем с самого верха,
    // игнорируя возможный hash из URL.
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
      const main = document.getElementById("main-scroll-container");
      if (main) {
        main.scrollTop = 0;
      }
      return;
    }

    // Desktop: прежнее поведение с якорями
      const hash = location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.hash]);

  const platformsInitialValues = useMemo(() => {
    const values: Record<string, boolean> = {};
    platforms.forEach((platform) => {
      const isChecked = isArray(release?.platforms) && release?.platforms.some((selected: { id: string }) => selected.id === platform.id) || false;
      values[`platform-${platform.id}`] = isChecked;
    });
    setAllData(isArray(release?.platforms) && release?.platforms.length === 67);
    return values;
  }, [platforms, release?.platforms]);

  const digitalFingerprintingSystemsInitialValues = useMemo(() => {
    const values: Record<string, boolean> = {};
    digitalFingerprintingSystems.forEach((system) => {
      values[`system-${system.name}`] = true;
    });
    return values;
  }, [platforms]);

  const onBack = () => {
    (async () => {
      try {
        await runDraftSaveIfNeeded();
        navigate(`/releases/new-release/${id}?step=track-upload`);
      } catch (err) {
        console.error("[Publish] draft save failed; navigation cancelled", err);
      }
    })();
  };

  const sanitizeCountryIds = (raw: Array<number | string>, allowed?: number[]) => {
    const allowSet = allowed ? new Set(allowed) : null;
    const cleaned = raw
      .map(n => Number(n))
      .filter(n => Number.isInteger(n) && n > 0 && (!allowSet || allowSet.has(n)));

    return Array.from(new Set(cleaned));
  };

  const extractInvalidIdsFrom422 = (err: any): number[] => {
    const out: number[] = [];
    const errors = err?.response?.data?.errors || err?.errors || {};
    Object.keys(errors).forEach(k => {
      const m = k.match(/^country_ids\.(\d+)$/);
      if (m) out.push(Number(m[1]));
    });
    return out;
  };

  const onSubmit = useCallback(async (values: FormValues) => {
    const platformIds = Object.entries(values.platforms)
      .filter(([, checked]) => checked)
      .map(([k]) => k.replace("platform-", ""));

    const uniquePlatformIds = Array.from(new Set([...(platformIds || []), ...((ugcData as string[]) || [])]));
    const rawCountryIds = extractSecondNumber(idData) as Array<number | string>;
    let countryIds = sanitizeCountryIds(rawCountryIds);

    try {
      await updateRelease({
        platform_ids: uniquePlatformIds,
        country_ids: countryIds,
      });
      navigate(`/releases/new-release/${id}?step=platform`);
    } catch (err: any) {
      const invalid = extractInvalidIdsFrom422(err);
      if (invalid.length) {
        const invalidSet = new Set(invalid);
        const fixedCountryIds = countryIds.filter(id => !invalidSet.has(id));

        if (fixedCountryIds.length !== countryIds.length) {
          await updateRelease({
            platform_ids: uniquePlatformIds,
            country_ids: fixedCountryIds,
          });
          navigate(`/releases/new-release/${id}?step=platform`);
          return;
        }
      }

      throw err;
    }
  }, [id, navigate, updateRelease, ugcData, idData]);

  const latestValuesRef = React.useRef<FormValues | null>(null);

  const initialPlatformSig = useMemo(() => {
    const ids = Array.isArray((release as any)?.platforms)
      ? (release as any).platforms.map((p: any) => String(p?.id)).filter(Boolean)
      : [];
    return Array.from(new Set(ids)).sort().join(",");
  }, [(release as any)?.platforms]);

  const initialCountrySig = useMemo(() => {
    const ids = Array.isArray((release as any)?.countries)
      ? (release as any).countries.map((c: any) => Number(c?.id)).filter((n: any) => Number.isFinite(n) && n > 0).map(String)
      : [];
    return Array.from(new Set(ids)).sort().join(",");
  }, [(release as any)?.countries]);

  const computeCurrentSigs = (values: FormValues) => {
    const platformIds = Object.entries((values as any)?.platforms || {})
      .filter(([, checked]) => !!checked)
      .map(([k]) => String(k).replace("platform-", ""));
    const uniquePlatformIds = Array.from(new Set([...(platformIds || []), ...((ugcData as string[]) || [])]))
      .filter(Boolean)
      .sort();

    const rawCountryIds = extractSecondNumber(idData) as Array<number | string>;
    const countryIds = sanitizeCountryIds(rawCountryIds).map(String).sort();

    return {
      platformSig: uniquePlatformIds.join(","),
      countrySig: countryIds.join(","),
      uniquePlatformIds,
      countryIds: countryIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0),
    };
  };

  const saveDraftOnly = useCallback(async () => {
    const values = latestValuesRef.current;
    if (!values) return;
    const { uniquePlatformIds, countryIds } = computeCurrentSigs(values);
    if (!uniquePlatformIds.length && !countryIds.length) return;

    try {
      await updateRelease({
        platform_ids: uniquePlatformIds,
        country_ids: countryIds,
      });
    } catch (err: any) {
      const invalid = extractInvalidIdsFrom422(err);
      if (invalid.length) {
        const invalidSet = new Set(invalid);
        const fixedCountryIds = (countryIds || []).filter((cid) => !invalidSet.has(cid));
        await updateRelease({
          platform_ids: uniquePlatformIds,
          country_ids: fixedCountryIds,
        });
        return;
      }
      throw err;
    }
  }, [ugcData, idData, updateRelease]);

  const isDirty = useCallback(() => {
    const values = latestValuesRef.current;
    if (!values) return false;
    const { platformSig, countrySig } = computeCurrentSigs(values);
    return platformSig !== initialPlatformSig || countrySig !== initialCountrySig;
  }, [ugcData, idData, initialPlatformSig, initialCountrySig]);

  useEffect(() => {
    setDraftSaveGuard({ isDirty, save: saveDraftOnly });
    return () => setDraftSaveGuard(null);
  }, [isDirty, saveDraftOnly]);

  const continentMap: Record<string, number> = { AFRICA: 4, AMERICAS: 4, EUROPE: 4, OCEANIA: 4, "": 0 };

  useEffect(() => {
    const countriesArray = release?.countries as unknown as Country[];
    const data: Record<string, CheckedItem> = {};
    countriesArray?.forEach((country) => {
      const continentId = continentMap[country.continent] || 0;
      if (!continentId) return;
      data[`${continentId}-${country.id}`] = { checked: true, partialChecked: false };
    });
    setResult(data)
  }, [release?.countries]);

  return (
    <SmartForm<FormValues>
      onSubmit={onSubmit}
      decorators={[decorators]}
      initialValues={{
        platformGroup: DEFAULT_PLATFORM_GROUP,
        checkAllCheckboxes: allData,
        platforms: platformsInitialValues,
        UGCPlatforms: platformsInitialValues,
        digitalFingerprintingSystem: digitalFingerprintingSystemsInitialValues,
      }}
      validate={structuredValidator({
        platforms: (val: any) => isNotObjectEmpty(val) || Object.values(val || {}).some(Boolean),
        // distributionCountry: release?.countries?.length > 0 ? undefined : (val:any) => isNotObjectEmpty(val) || Object.values(val || {}).some(Boolean),
      })}

    >
      {({ handleSubmit, values, hasValidationErrors }) => {
        latestValuesRef.current = values;
        const showPlatforms: any[] = [];
        if (values.platformGroup && values.platformGroup !== DEFAULT_PLATFORM_GROUP) {
          const platformGroup = platformGroups.find((platform) => platform.id === values.platformGroup);
          
          if (platformGroup) showPlatforms.push(...platformGroup.platforms);
        } else showPlatforms.push(...platforms);


        const mainPlatformList = showPlatforms.filter((p) => !UGC_PLATFORM_NAMES.some(keyword => p.name?.toLowerCase().includes(keyword)));

        return (
          <Container>
            <Header
              title={<FormattedMessage id="NewRelease.Publish.Title1" defaultMessage="Distribution platforms and countries (Step 6/6)" />}
              breadcrumbsText={<FormattedMessage id="NewRelease.Publish.Breadcrumbs" defaultMessage="Track information" />}
              onBack={onBack}
            />
            <FormContainer>
              {platformsFetching || countryFetching ? (
                <ProgressSpinnerContainer><ProgressSpinner /></ProgressSpinnerContainer>
              ) : (
                <>
                  {platformGroupsFetching ? <ProgressSpinnerContainer><ProgressSpinner /></ProgressSpinnerContainer> :
                    <Platforms
                      upDatePlatformGroup={upDatePlatformGroup}
                      platforms={platforms.filter(val => val.ugc === false)}
                      platformGroupsCreating={platformGroupsCreating}
                      mainPlatformList={mainPlatformList}
                      platformGroups={platformGroups}
                      createPlatformGroup={createPlatformGroup}
                      removePlatformGroup={removePlatformGroup}
                      selectedValues={selectedValues}
                      setSelectedValues={setSelectedValues}
                      setValidButton={setValidButton}
                    />}

                  {countryGroupsFetching ? (
                    <ProgressSpinnerContainer>
                      <ProgressSpinner />
                    </ProgressSpinnerContainer>
                  ) : (
                    <DistributionCountry
                    selectedValues={selectedValues}
                    platformGroups={platformGroups}
                    setUgcData={setUgcData}
                    setIdData={setIdData}
                      data={(release as any)?.countries || []}
                    removePlatformGroup={removePlatformGroup}
                    createPlatformGroup={createPlatformGroup}
                      ugcPlatformList={platforms.filter((val) => val.ugc === true)}
                    platformGroupsCreating={platformGroupsCreating}
                    UGC_PLATFORM_NAMES={UGC_PLATFORM_NAMES}
                    countryGroupsCreating={countryGroupsCreating}
                    countries={countries}
                    countryGroups={countryGroups}
                    updateCountryGroup={updateCountryGroup}
                    createCountryGroup={createCountryGroup}
                    removeCountryGroup={removeCountryGroup}
                      ugcPlatforms={(release as any)?.platforms || []}
                    setSelectedValuesCountry={setSelectedValuesCountry}
                    selectedValuesCountry={selectedValuesCountry}
                    />
                  )}
                </>

              )}

            </FormContainer>
            <FooterWrapper>
              <ButtonHoverZone
              onMouseEnter={() => setShowHint(true)}
              onMouseLeave={() => setShowHint(false)}
            >
              {showHint && !validButton && (
                <HoverPopup>
                  <HoverPopupTitleBox>
                    <Danger /> Select at least one platform to continue
                  </HoverPopupTitleBox>
                </HoverPopup>
              )}
              <Footer disabled={!validButton } nextButtonText="Check and publish" onNext={handleSubmit} />
              </ButtonHoverZone>
            </FooterWrapper>
          </Container>

        );
      }}
    </SmartForm>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 38px 20px 30px;
   min-height: 100%;
`;

const FormContainer = styled.div``;

const ProgressSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 24px;
`;
const FooterWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
`;

const ButtonHoverZone = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const HoverPopup = styled.div`
  position: absolute;
  bottom: 120%;
  right: 0;
  width: 319px;
  
  height: auto;
  opacity: 1;
  border-radius: 15px;
  background: rgba(226, 229, 255, 1);
  box-shadow: 0px 0px 10px 0px rgba(145, 145, 145, 0.47);
  padding: 17px 21px 14px 14px;
  display: flex;
  flex-direction: column;
  gap:9px;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HoverPopupTitleBox = styled.div`
  display: flex;
  gap:6px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 15px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0%;
  color:rgba(0, 0, 0, 1);
`

const Text = styled.div`
  font-family: SF Pro Display;
  font-weight: 400;
  font-style: Regular;
  font-size: 15px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0%;
  color:rgba(0, 0, 0, 1);
`
const TextBox = styled.div`
`